import { Maybe } from 'types/UtilityTypes';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { getSuggestionFormattedLabel } from 'utils/suggestions/suggestionFormat';

/** Preview tooltip section ids (must match config sectionOrder / sectionLabels). */
export enum KitPreviewSection {
  Pickup = 'pickup',
  Delivery = 'delivery',
  Consignee = 'consignee',
  Customer = 'customer',
  RateData = 'rateData',
  Specifications = 'specifications',
  Quote = 'quote',
  Carrier = 'carrier',
  CheckCall = 'checkCall',
}

export type SuggestionPreviewConfig = {
  sectionOrder: KitPreviewSection[];
  sectionLabels: Partial<Record<KitPreviewSection, string>>;
};

const LOAD_BUILDING_CONFIG: SuggestionPreviewConfig = {
  sectionOrder: [
    KitPreviewSection.Pickup,
    KitPreviewSection.Delivery,
    KitPreviewSection.Consignee,
    KitPreviewSection.Customer,
    KitPreviewSection.RateData,
    KitPreviewSection.Specifications,
  ],
  sectionLabels: {
    [KitPreviewSection.Pickup]: 'Pickup',
    [KitPreviewSection.Delivery]: 'Delivery',
    [KitPreviewSection.Consignee]: 'Consignee',
    [KitPreviewSection.Customer]: 'Customer',
    [KitPreviewSection.RateData]: 'Rates',
    [KitPreviewSection.Specifications]: 'Specifications',
  },
};

const QUICK_QUOTE_CONFIG: SuggestionPreviewConfig = {
  sectionOrder: [
    KitPreviewSection.Pickup,
    KitPreviewSection.Delivery,
    KitPreviewSection.Quote,
  ],
  sectionLabels: {
    [KitPreviewSection.Pickup]: 'Pickup',
    [KitPreviewSection.Delivery]: 'Delivery',
    [KitPreviewSection.Quote]: 'Quote Details',
  },
};

const CARRIER_INFO_CONFIG: SuggestionPreviewConfig = {
  sectionOrder: [KitPreviewSection.Carrier],
  sectionLabels: {
    [KitPreviewSection.Carrier]: 'Carrier',
  },
};

const APPT_CONFIRMATION_CONFIG: SuggestionPreviewConfig = {
  sectionOrder: [KitPreviewSection.Pickup, KitPreviewSection.Delivery],
  sectionLabels: {
    [KitPreviewSection.Pickup]: 'Pickup',
    [KitPreviewSection.Delivery]: 'Delivery',
  },
};

const CHECK_CALL_CONFIG: SuggestionPreviewConfig = {
  sectionOrder: [KitPreviewSection.CheckCall],
  sectionLabels: {
    [KitPreviewSection.CheckCall]: 'Check Call',
  },
};

export const SUGGESTION_PREVIEW_CONFIG: Record<
  SuggestionPipelines,
  SuggestionPreviewConfig
> = {
  [SuggestionPipelines.LoadBuilding]: LOAD_BUILDING_CONFIG,
  [SuggestionPipelines.QuickQuote]: QUICK_QUOTE_CONFIG,
  [SuggestionPipelines.CarrierInfo]: CARRIER_INFO_CONFIG,
  [SuggestionPipelines.ApptConfirmation]: APPT_CONFIRMATION_CONFIG,
  [SuggestionPipelines.CheckCall]: CHECK_CALL_CONFIG,
};

export function getSuggestionPreviewConfig(
  pipeline: SuggestionPipelines
): SuggestionPreviewConfig {
  return SUGGESTION_PREVIEW_CONFIG[pipeline];
}

export function getSectionNameForPipeline(
  fieldName: string,
  pipeline: SuggestionPipelines
): Maybe<KitPreviewSection> {
  switch (pipeline) {
    case SuggestionPipelines.LoadBuilding:
      if (fieldName.startsWith('pickup.')) return KitPreviewSection.Pickup;
      if (
        fieldName.startsWith('consignee.') ||
        fieldName.startsWith('dropoff.')
      ) {
        return KitPreviewSection.Consignee;
      }
      if (fieldName.startsWith('customer.')) return KitPreviewSection.Customer;
      if (fieldName === 'poNums') return KitPreviewSection.Customer;
      if (fieldName.startsWith('rateData.')) return KitPreviewSection.RateData;
      if (fieldName.startsWith('specifications.')) {
        return KitPreviewSection.Specifications;
      }
      if (fieldName.includes('pickup') && !fieldName.includes('.')) {
        return KitPreviewSection.Pickup;
      }
      if (fieldName.includes('delivery') && !fieldName.includes('.')) {
        return KitPreviewSection.Delivery;
      }
      if (fieldName.includes('customer') && !fieldName.includes('.')) {
        return KitPreviewSection.Customer;
      }
      return null;

    case SuggestionPipelines.QuickQuote:
      if (fieldName.startsWith('pickup')) return KitPreviewSection.Pickup;
      if (fieldName.startsWith('delivery')) return KitPreviewSection.Delivery;
      return KitPreviewSection.Quote;

    case SuggestionPipelines.CarrierInfo:
      return KitPreviewSection.Carrier;

    case SuggestionPipelines.ApptConfirmation:
      if (fieldName.includes('pickup') || fieldName === 'pickupApptTime') {
        return KitPreviewSection.Pickup;
      }
      if (fieldName.includes('dropoff') || fieldName === 'dropoffApptTime') {
        return KitPreviewSection.Delivery;
      }
      return null;

    case SuggestionPipelines.CheckCall:
      return KitPreviewSection.CheckCall;

    default:
      return null;
  }
}

export function hasPreviewableSuggestionChanges(
  flattenedChanges: Record<string, unknown>,
  pipeline: SuggestionPipelines
): boolean {
  return Object.entries(flattenedChanges).some(([name, val]) => {
    // Match getValidChangeElements: skip empty / zero
    if (!val) return false;
    return !!getSuggestionFormattedLabel(pipeline, name);
  });
}

export function stripPreviewSectionLabelPrefix(
  label: string,
  sectionKey: string
): string {
  const prefixes = [
    sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1),
    'Consignee',
    'Pickup',
    'Delivery',
    'Customer',
    'Carrier',
    'Check Call',
  ];

  let stripped = label;
  for (const prefix of prefixes) {
    if (stripped.startsWith(`${prefix} `)) {
      stripped = stripped.slice(prefix.length + 1);
      break;
    }
  }

  // Compact "Appointment ..." field labels in Kit previews (e.g. Appt. Start Time)
  if (stripped.startsWith('Appointment ')) {
    return `Appt. ${stripped.slice('Appointment '.length)}`;
  }

  return stripped;
}
